<!--
    Session Timeout Modal Component
    <session-timeout-modal-component></session-timeout-modal-component>
-->

<template>
  <div v-if="reauthenticate" class="session-timeout-modal-component" role="dialog" aria-modal="true" aria-labelledby="session-timeout-title">
    <div class="session-timeout-modal">
      <h3 id="session-timeout-title">Your session has timed out</h3>

      <form v-if="isPasswordLogin" @submit.prevent="submitPassword">
        <p>For your security, please enter your password to continue.</p>

        <!-- Hidden, but lets password managers match the saved login -->
        <input type="text" id="session-timeout-username" :value="reauthenticate.username" autocomplete="username" readonly hidden>

        <div class="mb-3 text-start">
          <label for="session-timeout-password" class="form-label">Password</label>
          <input type="password" class="form-control" id="session-timeout-password" ref="passwordInput" v-model="password" autocomplete="current-password" required>
        </div>

        <p v-if="errorMessage" class="session-timeout-error">{{ errorMessage }}</p>

        <button type="submit" class="button button--steelBlue w-100" :disabled="submitting">
          {{ submitting ? 'Signing in...' : 'Continue' }}
        </button>
      </form>

      <div v-else>
        <p>For your security, please sign in again to carry on where you left off.</p>

        <p v-if="errorMessage" class="session-timeout-error">{{ errorMessage }}</p>

        <button type="button" class="button button--steelBlue w-100" @click="signInWithAdfs">Sign in again</button>
      </div>

      <button type="button" class="btn btn-link session-timeout-logout" @click="logout">Log out instead</button>
    </div>
  </div>
</template>

<script setup>
import {computed, getCurrentInstance, nextTick, onBeforeUnmount, onMounted, ref} from 'vue';
import {EventBus} from '@/event-bus.js';

// Must match the window name checked in adfs-postflight-component
const ADFS_POPUP_WINDOW_NAME = 'origo-reauthenticate';

const {proxy} = getCurrentInstance();

const reauthenticate = ref(null);
const password = ref('');
const errorMessage = ref(null);
const submitting = ref(false);
const passwordInput = ref(null);

let adfsPopup = null;

const isPasswordLogin = computed(() => reauthenticate.value?.method === 'password');

const open = async (reauthenticateDetails) => {
  reauthenticate.value = reauthenticateDetails;
  password.value = '';
  errorMessage.value = null;

  await nextTick();
  passwordInput.value?.focus();
};

const complete = () => {
  reauthenticate.value = null;
  password.value = '';
  EventBus.$emit('session-reauthenticated');
};

const submitPassword = () => {
  submitting.value = true;
  errorMessage.value = null;

  proxy.$http.post('/api/reauthenticate', {password: password.value})
    .then(() => complete())
    .catch(err => {
      if (err.response?.status === 401) {
        logout();
        return;
      }

      errorMessage.value = err.response?.data?.message ?? 'Unable to sign in. Please try again.';
    })
    .finally(() => {
      submitting.value = false;
    });
};

const signInWithAdfs = () => {
  errorMessage.value = null;
  adfsPopup = window.open('/adfs-login', ADFS_POPUP_WINDOW_NAME, 'width=600,height=700');

  // Popup blocked - fall back to the full SSO redirect
  if (!adfsPopup) {
    window.location.assign('/adfs-login');
  }
};

const onAdfsPopupMessage = (event) => {
  if (event.origin !== window.location.origin || event.source !== adfsPopup || event.data?.type !== 'origo-reauthenticated') {
    return;
  }

  adfsPopup = null;
  complete();
};

// A full page load discards the held requests, so the user starts again from the login page
const logout = () => {
  proxy.$http.get('/logout').finally(() => {
    window.location.assign('/login');
  });
};

onMounted(() => {
  EventBus.$on('session-timeout', open);
  window.addEventListener('message', onAdfsPopupMessage);
});

onBeforeUnmount(() => {
  EventBus.$off('session-timeout', open);
  window.removeEventListener('message', onAdfsPopupMessage);
});
</script>
